import { useCallback, useMemo } from "react";

import ApplicationsConfig from "app/config/applications";
import { GenericError } from "shared/constants/errorTypes";
import { getMicrosoftSecurityToken } from "app/auth/authService";
import unfetch from "isomorphic-unfetch";
import { useErrors } from "shared/hooks/errors/useErrors";

export const useApiService = () => {
    const { handleError } = useErrors();

    const msalError = useMemo(() => new GenericError("Microsoft Access Token failure", {
        status: 401,
        title: "Authentication Error",
        subTitle: "Unable to obtain access token",
        correlationId: null,
        recoverable: false,
        showDetail: true,
    }), []);

    // Token Validation
    const validateAndGetMicrosoftAccessToken = useCallback(async () => {
        const accessToken = await getMicrosoftSecurityToken();

        if (accessToken === null) {
            console.log("Bestwork - Microsoft Access Token failure");
            handleError(msalError);
            return null;
        }

        return accessToken;
    }, [handleError, msalError]);

    // Main Backend Call
    const callBackend = useCallback(async (api, apiversion, resource, options) => {
        const accessToken = await validateAndGetMicrosoftAccessToken();
        if (!accessToken) {
            return;
        }

        const headers = buildRequestHeaders(accessToken);
        const url = buildRequestURL(api, apiversion, resource, { ...options.params }, options.filters);
        const fetchOptions = buildFetchOptions(options, headers);

        try {
            const payload = await unfetch(url, fetchOptions);
            // If the server returns 4XX or 5XX errors it's not going to the catch.
            // We are handling those errors in the validateResponse function (useApi, that is the caller).
            return payload;
        } catch (error) {
            // In this catch we are handling network errors. For example, when the backend is unreachable.
            console.error("callBackend - error - ", error);
            handleError(error);
        }
    }, [handleError, validateAndGetMicrosoftAccessToken]);

    // Request Builders
    const buildRequestHeaders = (accessToken) => {
        const headers = {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
        };

        return headers;
    };

    const buildRequestURL = (api, apiversion, resource, params, filters) => {
        const queryParams = Object.keys(params).map((key, index) => {
            const separator = index === 0 ? "?" : "&";
            return `${separator}${key}=${params[key]}`;
        }).join("");

        let url = `${api}/api/${apiversion}/${resource}${queryParams}`;

        if (filters) {
            url = `${url}&${filters.join("&")}`;
        }

        return url;
    };

    const buildFetchOptions = (options, headers) => {
        return {
            method: options.method || "GET",
            headers,
            body: options.body ? JSON.stringify(options.body) : null,
        };
    };

    // Skill Search API
    const callSkillSearch = useCallback(async (resource, options) => {
        try {
            const response = await fetch(ApplicationsConfig.SkillSearch.backend + resource, options);
            return await response.json();
        } catch (error) {
            console.log("Skill Search API Error:", error);
        }
    }, []);

    return useMemo(() => ({ 
        callSkillSearch, 
        callBackend 
    }), [callSkillSearch, callBackend]);
};
